/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createAsyncThunk,
  createAction,
  ActionReducerMapBuilder,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { difference } from 'lodash';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';
import { QueryService } from '@vk-hr-tek/core/query';

import { CancelReasonItem, EventListItem as Event } from '@app/gen/events';
import { ThunkExtra } from '@app/store';

import {
  EventsState,
  EventsWithRootState,
  startLoading,
  completeLoading,
  setError,
  eventsAdapter,
} from '../events.state';
import { GetEventsDto } from '../../dto';
import { EventsDetailService, EventsListService } from '../../services';

export const setFilters = createAction<{
  id: string;
  data: Filter[] | null;
}>('events/setFilters');

export const getEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    parentEventTags: string[];
    finished: number;
    tags?: string[];
    companyId?: string;
    isAbsencesPage?: boolean;
    showUrgent?: boolean;
  },
  GetEventsDto & { isAbsencesPage?: boolean },
  ThunkExtra<EventsWithRootState>
>(
  'events/getEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      const filtersData = {
        tags: getEventsDto.tags,
        companyId: getEventsDto.companyId,
      };

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: getEventsDto.isAbsencesPage
            ? await eventsService.getAbsenceFilters(filtersData)
            : await eventsService.getFilters(filtersData),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.get(getEventsDto, filters.data as Filter[]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    finished: number;
  },
  GetEventsDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/updateEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const filters = getState().events.filters.data;
      const showUrgent = getState().events.showUrgentEvents;
      const eventListService = inject(EventsListService);

      const filter = inject(QueryService).parse(history.location.search);

      const result = await eventListService.get(
        {
          ...getEventsDto,
          showUrgent,
          ...(filter.filters ? { filters: filter.filters } : {}),
        },
        filters || [],
      );

      return result;
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось обновить заявки',
            message: 'Пожалуйста, попробуйте перезагрузить страницу',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getDocumentCount = createAsyncThunk<
  {
    totalCount: number;
  },
  GetEventsDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/getDocumentCount',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters(),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.getDocumentCount(
        getEventsDto,
        filters.data || [],
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
  },
  GetEventsDto & { isAbsencesPage?: boolean },
  ThunkExtra<EventsWithRootState>
>(
  'events/getAdditionalEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);

      let filters = state.filters;
      const showUrgent = state.showUrgentEvents;

      const filtersData = {
        tags: getEventsDto.tags,
        companyId: getEventsDto.companyId,
      };

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: getEventsDto.isAbsencesPage
            ? await eventsService.getAbsenceFilters(filtersData)
            : await eventsService.getFilters(filtersData),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.get(
        { ...getEventsDto, showUrgent },
        filters.data || [],
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCancelReasons = createAsyncThunk<
  CancelReasonItem[],
  { isCandidate?: boolean } | undefined,
  ThunkExtra<EventsWithRootState>
>(
  'events/getCancelReasons',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventService = inject(EventsDetailService);
      const result = await eventService.getCancelReasons({
        isCandidate: params?.isCandidate,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setShowUrgentEvents = createAction<boolean>(
  'events/setShowUrgentEvents',
);

export const eventsListReducers = (
  builder: ActionReducerMapBuilder<EntityState<Event, string> & EventsState>,
) => {
  builder.addCase(setFilters, (state, action) => {
    state.filters = action.payload;
  });
  builder.addCase(getEvents.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(getEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.total = payload.total;
    state.totalMy = payload.totalMy;
    state.totalMyTeam = payload.totalMyTeam;
    state.parentEventTags = payload.parentEventTags;
    state.finished = payload.finished;
    state.currentIds = payload.data.map(({ id }) => id);
    eventsAdapter.upsertMany(state, payload.data);
  });

  builder.addCase(getAdditionalEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.total = payload.total;
    state.currentIds = [
      ...state.currentIds,
      ...payload.data.map(({ id }) => id),
    ];
    eventsAdapter.upsertMany(state, payload.data);
  });
  builder.addCase(getEvents.rejected, (state, { payload, error, meta }) => {
    if (!meta.aborted) {
      setError(state, { payload, error });
    }
  });
  builder.addCase(updateEvents.fulfilled, (state, { payload, meta }) => {
    completeLoading(state);

    state.total = payload.total;
    state.totalMy = payload.totalMy;
    state.totalMyTeam = payload.totalMyTeam;
    state.finished = payload.finished;

    const removedIds = difference(
      meta.arg.sortByIds || [],
      payload.data.map(({ id }) => id),
    );

    eventsAdapter.updateMany(
      state,
      payload.data.map((event) => ({
        id: event.id,
        changes: event,
      })),
    );

    eventsAdapter.removeMany(state, removedIds);
  });

  builder.addCase(getDocumentCount.pending, (state) => {
    state.totalDocuments = null;
  });
  builder.addCase(getDocumentCount.fulfilled, (state, { payload }) => {
    state.totalDocuments = payload.totalCount;
  });

  builder.addCase(getCancelReasons.pending, (state) => {
    state.cancelReasons = null;
  });
  builder.addCase(getCancelReasons.fulfilled, (state, { payload }) => {
    state.cancelReasons = payload;
  });

  builder.addCase(setShowUrgentEvents, (state, { payload }) => {
    state.showUrgentEvents = payload;
  });
};
